import { useCallback, useEffect, useRef, useState } from 'react';
import { moment } from 'Imports';
import { VideoEventResponse } from '$Generated/api';
import { splitTreatmentNames, useGetSplitTreatment } from '$Utilities/split-io-sdk';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { VideoEventService } from '$State/VideoEventFreezerService';
import { AiOverlayContextValue } from './AiOverlayContext';
import { isAiOverlayEligibleEventType } from '$Utilities/aiOverlayEligibleEventTypes';

const AI_OVERLAY_MAX_AGE_DAYS = 30;
const AI_OVERLAY_POLL_INTERVAL_MS = 4000;
const AI_OVERLAY_POLL_TIMEOUT_MS = 120000;

export const useAiOverlayState = (
    eventId: string,
    videoEvent: VideoEventResponse | undefined,
    onVideoEventRefreshed: () => Promise<void>,
): AiOverlayContextValue => {
    const [aiOverlayOn, setAiOverlayOn] = useState(false);
    const [aiOverlayLoading, setAiOverlayLoading] = useState(false);
    const [aiOverlaySplitOn, setAiOverlaySplitOn] = useState(false);
    const [roleAllowed, setRoleAllowed] = useState(false);
    const [hasAiOverlayEverBeenOn, setHasAiOverlayEverBeenOn] = useState(false);

    const { getSplitTreatment } = useGetSplitTreatment();
    const { showSnackbarMessage } = useAppSnackbar();

    const pollIntervalRef = useRef<ReturnType<typeof setInterval>>();
    const pollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const cancelledRef = useRef(false);

    const stopPolling = useCallback(() => {
        clearInterval(pollIntervalRef.current);
        clearTimeout(pollTimeoutRef.current);
    }, []);

    useEffect(() => {
        const getSplitStatus = async () => {
            setAiOverlaySplitOn(await getSplitTreatment(splitTreatmentNames.aiOverlay));
        };

        getSplitStatus().catch(() => {
            setAiOverlaySplitOn(false);
        });

        setRoleAllowed(canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo]));
    }, []);

    useEffect(() => {
        if (aiOverlayOn) {
            setHasAiOverlayEverBeenOn(true);
        }
    }, [aiOverlayOn]);

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            stopPolling();
        };
    }, [stopPolling]);

    const fail = useCallback(() => {
        stopPolling();
        if (cancelledRef.current) return;
        setAiOverlayLoading(false);
        setAiOverlayOn(false);
        showSnackbarMessage({
            message: 'AI Overlay could not be loaded. Please try again.',
            alertType: AlertTypes.Fail,
        });
    }, [showSnackbarMessage, stopPolling]);

    const beginRetrieval = useCallback(async () => {
        setAiOverlayLoading(true);

        try {
            await VideoEventService.requestAiOverlay(eventId);
        } catch {
            fail();
            return;
        }

        pollTimeoutRef.current = setTimeout(fail, AI_OVERLAY_POLL_TIMEOUT_MS);

        pollIntervalRef.current = setInterval(async () => {
            try {
                await VideoEventService.getAiVideoAvailable(eventId);
                const isAvailable = VideoEventService.getState().aiVideoAvailableResult.data === true;

                if (isAvailable) {
                    stopPolling();
                    await onVideoEventRefreshed();
                    if (!cancelledRef.current) {
                        setAiOverlayLoading(false);
                    }
                }
            } catch {
                // A single transient poll failure shouldn't kill an otherwise still-in-progress retrieval; only the hard timeout above does.
            }
        }, AI_OVERLAY_POLL_INTERVAL_MS);
    }, [eventId, fail, onVideoEventRefreshed, stopPolling]);

    const hasAiVideoAsset = videoEvent?.videoAsset?.some((asset) => !!asset.aiVideoUrl) ?? false;

    const toggleAiOverlay = useCallback(() => {
        if (aiOverlayOn) {
            stopPolling();
            setAiOverlayLoading(false);
            setAiOverlayOn(false);
            return;
        }

        setAiOverlayOn(true);

        if (!hasAiVideoAsset) {
            beginRetrieval();
        }
    }, [aiOverlayOn, hasAiVideoAsset, beginRetrieval, stopPolling]);

    const eventAgeDays = videoEvent?.eventStartDate ? moment().diff(moment(videoEvent.eventStartDate), 'days') : 0;

    // Events older than the retention window still show the toggle if the AI overlay was already retrieved for them.
    const showAiOverlayToggle =
        aiOverlaySplitOn &&
        roleAllowed &&
        isAiOverlayEligibleEventType(videoEvent) &&
        (eventAgeDays <= AI_OVERLAY_MAX_AGE_DAYS || hasAiVideoAsset);

    return { aiOverlayOn, aiOverlayLoading, showAiOverlayToggle, toggleAiOverlay, hasAiOverlayEverBeenOn };
};
